import { useCallback, useEffect, useMemo, useState } from 'react';
import Chip from '@mui/material/Chip';
import { useTheme } from '@mui/material/styles';
import { uniqBy } from 'lodash';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { toString } from 'utilities/toString';
import { SecuritiesChipContainerProps } from './SecuritiesChipContainer.props';
import { classes, StyledPaper } from './SecuritiesChipContainer.styles';
import { ReverseParsedFundOwnershipDetail } from '../../types';

interface SecurityChipData {
  key: string;
  id: number | string | null;
  label: string;
}

const toSecurityKey = (column: ReverseParsedFundOwnershipDetail) =>
  column.security ? `security-${column.security}` : `custom-${column.custom_security_name ?? ''}`;

const SecuritiesChipContainer = ({
  columns,
  setActiveSecuritiesColumns,
  reset,
}: SecuritiesChipContainerProps) => {
  const theme = useTheme();
  const [chipData, setChipData] = useState<SecurityChipData[]>([]);
  const [selectedSecurityKeys, setSelectedSecurityKeys] = useState<string[]>([]);
  const [{ captableInfo }] = useStore() as unknown as UseStoreValues;

  const securities = useMemo(() => {
    if (!columns?.length) {
      return [];
    }

    const captableSecurities = captableInfo?.securities ?? [];
    const chips = columns
      .map(column => {
        const securityId = column.security ?? null;
        if (!securityId) {
          const customLabel = column.custom_security_name ?? '';
          if (!customLabel) {
            return null;
          }
          return {
            key: `custom-${customLabel}`,
            id: column.id,
            label: customLabel,
          };
        }

        const matchedSecurity = captableSecurities.find(security => Number(security.id) === Number(securityId));
        const label = matchedSecurity?.name ?? column.security_name ?? '';

        if (!label) {
          return null;
        }

        return {
          key: `security-${securityId}`,
          id: securityId,
          label,
        };
      })
      .filter(Boolean) as SecurityChipData[];

    return uniqBy(chips, 'key');
  }, [captableInfo?.securities, columns]);

  useEffect(() => {
    setChipData(securities);
  }, [securities]);

  useEffect(() => {
    if (columns) {
      const filteredColumns = columns.filter(column => !selectedSecurityKeys.includes(toSecurityKey(column)));
      setActiveSecuritiesColumns(filteredColumns);
    }
  }, [columns, selectedSecurityKeys, setActiveSecuritiesColumns]);

  useEffect(() => {
    if (reset) {
      setSelectedSecurityKeys([]);
    }
  }, [reset]);

  const handleClick = useCallback(
    (selectedChip: SecurityChipData) => () => {
      const selectedKey = selectedChip.key;
      if (!selectedSecurityKeys.includes(selectedKey) && securities.length > selectedSecurityKeys.length + 1) {
        setSelectedSecurityKeys([...selectedSecurityKeys, selectedKey]);
      } else if (selectedSecurityKeys.includes(selectedKey)) {
        setSelectedSecurityKeys(selectedSecurityKeys.filter(key => key !== selectedKey));
      }
    },
    [securities, selectedSecurityKeys]
  );

  if (!chipData.length) {
    return null;
  }

  return (
    <StyledPaper className={classes.root}>
      {chipData.map(data => {
        const { key } = data;
        return (
          <div key={key}>
            <Chip
              id={toString(data.id)}
              label={data.label}
              onClick={handleClick(data)}
              className={classes.chip}
              style={
                selectedSecurityKeys.includes(key)
                  ? {
                    backgroundColor: theme.palette.primary.contrastText,
                    color: theme.palette.primary.main,
                  }
                  : {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                  }
              }
              clickable
            />
          </div>
        );
      })}
    </StyledPaper>
  );
};

export default SecuritiesChipContainer;
